import React, { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useTheme } from "../lib/theme";
import { useI18n } from "../lib/i18n";
import {
  LayoutGrid, Package, ShoppingCart, Truck,
  Camera, Users, Wallet, Coins, Shield, Building2, Webhook,
  Bell, FileBarChart2, Settings as SettingsIcon,
  Sun, Moon, Languages, LogOut, Menu, ChevronsLeft, ChevronsRight,
  ChevronDown, ScanLine, ShieldCheck, Upload, TrendingUp, Target, Radio, Layers, Mail, ListChecks, UserCog, ClipboardList, MapPinned,
} from "lucide-react";
import OfflineBar from "./OfflineBar";
import PageHelp from "./PageHelp";

// Nav organized by sections
const SECTIONS = [
  {
    key: "section_operations",
    items: [
      { to: "/dashboard", icon: LayoutGrid, key: "nav_dashboard", roles: ["admin","operator","deliverer","client"] },
      { to: "/imports", icon: Upload, key: "nav_imports", roles: ["admin","operator"] },
      { to: "/bipagem", icon: ScanLine, key: "nav_bipagem", roles: ["admin","operator"] },
      { to: "/operational", icon: ShoppingCart, key: "nav_operational", roles: ["admin","operator"] },
      { to: "/documents", icon: Package, key: "nav_documents", roles: ["admin","operator","client","deliverer"] },
      { to: "/email-ingest", icon: Mail, key: "nav_email_ingest", roles: ["admin","operator"] },
      { to: "/routes", icon: Truck, key: "nav_routes", roles: ["admin","operator"] },
      { to: "/tracking", icon: Radio, key: "nav_tracking", roles: ["admin","operator"] },
      { to: "/service-orders", icon: ClipboardList, key: "nav_service_orders", roles: ["deliverer","admin","operator"] },
    ],
  },
  {
    key: "section_tracking",
    items: [
      { to: "/evidences", icon: Camera, key: "nav_evidences", roles: ["admin","operator","deliverer"] },
      { to: "/portal", icon: Users, key: "nav_portal", roles: ["client","admin"] },
    ],
  },
  {
    key: "section_finance",
    items: [
      { to: "/financial", icon: Wallet, key: "nav_financial", roles: ["admin"] },
      { to: "/commissions", icon: Coins, key: "nav_commissions", roles: ["admin","operator","deliverer"] },
      { to: "/productivity", icon: TrendingUp, key: "nav_productivity", roles: ["admin","operator"] },
      { to: "/goals", icon: Target, key: "nav_goals", roles: ["admin"] },
    ],
  },
  {
    key: "section_governance",
    items: [
      { to: "/audit", icon: Shield, key: "nav_audit", roles: ["admin"] },
      { to: "/occurrences", icon: ListChecks, key: "nav_occurrences", roles: ["admin"] },
      { to: "/users", icon: UserCog, key: "nav_users", roles: ["admin"] },
      { to: "/tenants", icon: Building2, key: "nav_tenants", roles: ["admin"] },
      { to: "/sectors", icon: MapPinned, key: "nav_sectors", roles: ["admin"] },
      { to: "/webhooks", icon: Webhook, key: "nav_webhooks", roles: ["admin","operator"] },
      { to: "/security", icon: ShieldCheck, key: "nav_security", roles: ["admin","operator","deliverer","client"] },
    ],
  },
  {
    key: "section_system",
    items: [
      { to: "/notifications", icon: Bell, key: "nav_notifications", roles: ["admin","operator","deliverer","client"] },
      { to: "/reports", icon: FileBarChart2, key: "nav_reports", roles: ["admin","operator"] },
      { to: "/settings", icon: SettingsIcon, key: "nav_settings", roles: ["admin","operator","deliverer","client"] },
    ],
  },
];

function LogoTile() {
  return (
    <div className="w-11 h-11 rounded-2xl bg-[var(--brand)] flex items-center justify-center shadow-md shadow-[var(--brand-soft)] shrink-0">
      <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2 3 6.5v10L12 21l9-4.5v-10z"/><path d="M3 6.5 12 11l9-4.5"/><path d="M12 11v10"/>
      </svg>
    </div>
  );
}

export default function Layout({ children }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [expanded, setExpanded] = useState(() => localStorage.getItem("nafe_sidebar") !== "collapsed");
  const [collapsedSections, setCollapsedSections] = useState(() => {
    try { return JSON.parse(localStorage.getItem("nafe_sections") || "{}"); }
    catch { return {}; }
  });
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const { t, lang, setLang } = useI18n();
  const navigate = useNavigate();

  useEffect(() => {
    localStorage.setItem("nafe_sidebar", expanded ? "expanded" : "collapsed");
  }, [expanded]);

  useEffect(() => {
    localStorage.setItem("nafe_sections", JSON.stringify(collapsedSections));
  }, [collapsedSections]);

  const toggleSection = (key) => setCollapsedSections((s) => ({ ...s, [key]: !s[key] }));

  const role = user?.role || "client";
  const visibleSections = SECTIONS
    .map((s) => ({ ...s, items: s.items.filter((i) => i.roles.includes(role)) }))
    .filter((s) => s.items.length > 0);

  const sidebarW = expanded ? "w-64" : "w-[76px]";

  return (
    <div className="min-h-screen flex bg-[var(--bg-main)] text-foreground p-3 sm:p-4 gap-3 sm:gap-4">
      {mobileOpen && (
        <div className="fixed inset-0 bg-black/40 z-[400] lg:hidden" onClick={() => setMobileOpen(false)} data-testid="mobile-overlay" />
      )}

      <aside
        className={`${mobileOpen ? "translate-x-0" : "-translate-x-[110%]"} lg:translate-x-0 transition-all duration-300 fixed lg:sticky top-3 lg:top-4 left-3 lg:left-0 h-[calc(100vh-1.5rem)] lg:h-[calc(100vh-2rem)] ${expanded ? "w-64" : "lg:w-[76px] w-64"} bg-[var(--surface)] border border-[var(--border-c)] rounded-3xl flex flex-col py-4 z-[500] shadow-sm overflow-hidden`}
        data-testid="sidebar"
      >
        {/* Header with logo + toggle */}
        <div className={`flex shrink-0 ${expanded ? "flex-row items-center gap-3 px-4" : "flex-row items-center gap-3 px-4 lg:flex-col lg:items-center lg:gap-2 lg:px-2"}`}>
          <LogoTile />
          {expanded && (
            <div className="flex-1 leading-tight overflow-hidden min-w-0">
              <div className="font-display font-bold text-sm text-[var(--text-1)]">Nafe Logistica</div>
              <div className="text-[10px] text-[var(--text-2)]">Ops · Logistics · Finance</div>
            </div>
          )}
          <button
            onClick={() => setExpanded((v) => !v)}
            className={`hidden lg:flex w-7 h-7 rounded-lg bg-[var(--surface-2)] hover:bg-[var(--brand-soft)] hover:text-[var(--brand)] items-center justify-center text-[var(--text-2)] shrink-0 ${expanded ? "" : "lg:mt-0.5"}`}
            data-testid="sidebar-toggle"
            aria-label={expanded ? t("collapse_menu") : t("expand_menu")}
          >
            {expanded ? <ChevronsLeft size={14} /> : <ChevronsRight size={14} />}
          </button>
        </div>

        {/* Nav sections */}
        <nav className="flex-1 mt-6 overflow-y-auto pb-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {visibleSections.map((section, idx) => {
            const isCollapsed = expanded && !!collapsedSections[section.key];
            return (
              <div key={section.key} className={idx > 0 ? "mt-4" : ""}>
                {expanded && (
                  <button
                    type="button"
                    onClick={() => toggleSection(section.key)}
                    className="w-full flex items-center justify-between px-5 pb-2 group"
                    data-testid={`section-toggle-${section.key}`}
                  >
                    <span className="text-[10px] uppercase tracking-wider font-semibold text-[var(--text-3)] group-hover:text-[var(--text-2)]" data-testid={`section-${section.key}`}>
                      {t(section.key)}
                    </span>
                    <ChevronDown
                      size={12}
                      className={`text-[var(--text-3)] group-hover:text-[var(--text-2)] transition-transform ${isCollapsed ? "-rotate-90" : ""}`}
                    />
                  </button>
                )}
                {!expanded && idx > 0 && <div className="mx-4 my-3 border-t border-[var(--border-c)]" />}
                {!isCollapsed && (
                  <div className={`flex flex-col gap-1 ${expanded ? "px-3" : "items-center px-2"}`}>
                    {section.items.map(({ to, icon: Icon, key }) => (
                      <NavLink
                        key={to}
                        to={to}
                        end={to === "/" || to === "/dashboard"}
                        data-testid={`nav-${key.replace("nav_", "")}`}
                        onClick={() => setMobileOpen(false)}
                        title={!expanded ? t(key) : undefined}
                        className={({ isActive }) =>
                          `relative flex items-center ${expanded ? "gap-3 h-10 px-3 w-full" : "w-11 h-11 justify-center"} rounded-2xl transition-all group ${
                            isActive
                              ? "bg-[var(--brand-soft)] text-[var(--brand)] font-semibold"
                              : "text-[var(--text-2)] hover:bg-[var(--surface-2)] hover:text-[var(--text-1)]"
                          }`
                        }
                      >
                        {({ isActive }) => (
                          <>
                            {isActive && <span className={`absolute ${expanded ? "-left-3" : "left-0"} top-1/2 -translate-y-1/2 w-1 h-6 bg-[var(--brand)] rounded-r-full`} />}
                            <Icon size={19} strokeWidth={1.9} className="shrink-0" />
                            {expanded && <span className="text-sm truncate">{t(key)}</span>}
                          </>
                        )}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* User footer */}
        <div className={`flex shrink-0 pt-3 border-t border-[var(--border-c)] ${expanded ? "flex-row items-center gap-3 px-4" : "flex-row items-center gap-3 px-4 lg:flex-col lg:items-center lg:gap-2 lg:px-2"}`}>
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[var(--brand)] to-[var(--brand-2)] flex items-center justify-center text-white text-sm font-bold shrink-0" title={user?.name}>
            {(user?.name || "?").slice(0, 1).toUpperCase()}
          </div>
          {expanded && (
            <div className="flex-1 min-w-0 leading-tight">
              <div className="text-xs font-semibold truncate text-[var(--text-1)]">{user?.name}</div>
              <div className="text-[10px] text-[var(--text-2)] capitalize">{user?.role}</div>
            </div>
          )}
          <button
            onClick={async () => { await logout(); navigate("/login"); }}
            className={`w-9 h-9 rounded-full bg-[var(--surface-2)] hover:bg-[var(--danger-soft)] hover:text-[var(--danger)] flex items-center justify-center text-[var(--text-2)] shrink-0 ${expanded ? "" : "lg:w-10 lg:h-10"}`}
            data-testid="logout-button"
            title={t("logout")}
          >
            <LogOut size={15} />
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0 overflow-x-hidden lg:ml-0">
        <header className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4 relative z-10">
          <button onClick={() => setMobileOpen(true)} className="lg:hidden bg-[var(--surface)] border border-[var(--border-c)] rounded-2xl h-11 w-11 flex items-center justify-center shadow-sm" data-testid="mobile-menu-button" aria-label="Menu">
            <Menu size={18} />
          </button>
          <div className="flex-1" />
          <PageHelp />
          <button
            onClick={() => setLang(lang === "pt" ? "en" : "pt")}
            className="hidden sm:flex items-center gap-1.5 px-3 h-11 rounded-2xl bg-[var(--surface)] border border-[var(--border-c)] text-xs font-semibold text-[var(--text-2)] hover:text-[var(--text-1)] shadow-sm"
            data-testid="lang-toggle"
          >
            <Languages size={14} /> {lang.toUpperCase()}
          </button>
          <button
            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            className="flex items-center gap-1.5 px-3 h-11 rounded-2xl bg-[var(--surface)] border border-[var(--border-c)] text-[var(--text-2)] hover:text-[var(--text-1)] shadow-sm"
            data-testid="theme-toggle"
            aria-label="Toggle theme"
          >
            {theme === "dark" ? <Sun size={14} /> : <Moon size={14} />}
          </button>
        </header>

        <main className="flex-1 fade-up min-w-0 w-full relative z-0">{children}</main>
      </div>
      <OfflineBar />
    </div>
  );
}
